import { DotPadLine } from "./DotPadLine";
import { DotPadSendMakeModel, DotPadSendType } from "./DotPadSendMakeModel";

const RefreshType = {
    NONE: 'NONE',
    TYPE_A: 'TYPE_A',
    TYPE_B: 'TYPE_B',
    TYPE_C: 'TYPE_C',
};

const RefreshMode = {
    NONE: 'NONE',
    ALL_DISPLAY: 'ALL_DISPLAY',
    GRAPHIC_DISPLAY: 'GRAPHIC_DISPLAY',
    TEXT_DISPLAY: 'TEXT_DISPLAY',
    LIVE_DISPLAY: 'LIVE_DISPLAY',
};

class DotPadSendModule {
    constructor(dotPad) {
        this.dotPadLineList = [];
        this.dotCommandSendReady = true;
        this.sendTime = new Date();
        this.sendCnt = 0;
        this.dotPadLine = null;
        this.restart = false;
        this.currentIndex = 0;
        this.isFunctionRunning = false;
        this.dotPad = dotPad;
        this.baseDisplayComplete = true; // 오토리프레시를 제외한 최초출력이 완료되었는지 체크

        this.refreshType = RefreshType.TYPE_C;
        this.refreshCount = 0;
        this.refreshItem = null;
        this.refreshLine = [];
        this.refreshMode = RefreshMode.NONE;

        this.sendType = DotPadSendType.SINGLE_LINE
    }

    // 라인목록 초기화
    clearDotPadLine(){
        this.dotPadLineList = []
    }

    // 닷패드 라인 추가
    addDotPadLine(rowCellSize, colCellSize) {
        // 기존 설정과 동일한지 체크
        if (this.dotPadLineList.length > 0) {
            const existingLastLine = this.dotPadLineList[this.dotPadLineList.length - 1];
            
            // 마지막 라인 번호 셀 크기가 동일하면 재설정하지 않음
            if ( 
                existingLastLine && 
                existingLastLine.lineId === rowCellSize &&
                existingLastLine.numberCellColumns === colCellSize) {
                return;
            }
        }

        this.dotPadLineList = [];

        // 20셀 세팅 (600:requestTime)
        this.dotPadLineList.push(new DotPadLine(0, 600, 20));
        
        // 디바이스 크기에 맞게 세팅 (600:requestTime)
        for (let i = 1; i <= rowCellSize; i++) {
            this.dotPadLineList.push(new DotPadLine(i, 600, colCellSize));
        }
    }

    // 해당 라인에 커맨드 세팅
    setDotPadLineCommand(line, seqNum, startCellIndex, sendData, init = true) {
        if (this.dotPadLineList.length <= line) {
            return;
        }

        if (init) {
            if (this.refreshCount > 0) {
                for (const tempDotPadLine of this.dotPadLineList) {
                    tempDotPadLine.setRequestReady(false);
                    tempDotPadLine.setReceiveAck(true);
                }
            }

            this.refreshCount = 0;
            this.currentIndex = 0;
        } else {
            this.currentIndex = 0;
        }
        
        if (this.refreshMode === RefreshMode.LIVE_DISPLAY && !this.refreshLine.includes(line)){
            this.refreshLine.push(line);
            this.dotPadLineList[line].initRefreshLiveData(startCellIndex, sendData);
        } else {
            this.dotPadLineList[line].setRefreshLiveData(startCellIndex, sendData);
        }
        this.dotPadLineList[line].setCommand(seqNum, startCellIndex, sendData);
    }

    // 데이터 비교후 다른 부분만 출력
    setDotPadLineCommandDiff(line, seqNum, sendData, init = true) {
        if (this.dotPadLineList.length <= line) {
            return;
        }
        
        const result = this.compareString(sendData, this.dotPadLineList[line].getLineData());
        if (result !== null) {
            const [start, end] = result;
            const diffData = sendData.slice(start, end + 1);
            this.setDotPadLineCommand(line, seqNum, parseInt(start / 2), diffData, init);
            return true
        }

        return false
    }

    // 발송준비 세팅
    setDotCommandSendReady(ready) {
        this.dotCommandSendReady = ready
    }

    async refresh() {
        for (let i = 0; i < this.dotPadLineList.length; i++) {
            this.dotPadLineList[i].refresh();
        }

        await this.sendCommand();
    }
    
    checkBaseDisplay() {
        return (this.isFunctionRunning && this.currentIndex >= this.dotPadLineList.length - 1) || this.refreshCount > 0 || !this.isFunctionRunning
    }
    
    getIsFunctionRunning() {
        return this.isFunctionRunning
    }

    setDisplayComplete() {
        if (this.checkBaseDisplay()) {
            this.baseDisplayComplete = true
        }
    }

    // 최초출력 완료 체크
    getDisplayComplete() {
        this.setDisplayComplete()
        return this.baseDisplayComplete
    }

    setSendType(sendType) {
        this.sendType = sendType
    }

    getSendType() {
        return this.sendType
    }

    async sendCommand(refresh = false) {
        if (!this.isFunctionRunning) {
            this.isFunctionRunning = true
            this.currentIndex = 0

            if (!refresh)
                this.refreshCount = 0
            
            const cellType = this.dotPad.deviceInfo.cellType

            const processNextLine = async () => {
                if (this.currentIndex < this.dotPadLineList.length) {
                    // 재시작일경우 값 초기화
                    if (this.restart) {
                        this.currentIndex = 0
                        this.restart = false
                        this.dotCommandSendReady = true
                    }
                    else {
                        // 전체출력일때 시작라인 세팅
                        if (this.currentIndex > 0 && (this.sendType === DotPadSendType.ALL_LINE || this.sendType === DotPadSendType.ALL_PARTIAL)) {
                            let list = this.dotPadLineList.filter(line => line.getRequestReady());
                            this.currentIndex = list.length > 0 ? list[0].getLineId() : this.currentIndex;
                        } 

                        this.dotPadLine = this.dotPadLineList[this.currentIndex]

                        // 발송해야하는지 확인
                        if (this.dotPadLine.getRequestReady() && this.dotCommandSendReady) {
                            // 발송 처리
                            if (this.currentIndex === 0 || this.sendType === DotPadSendType.SINGLE_LINE || this.sendType === DotPadSendType.SINGLE_PARTIAL) {
                                this.dotPadLine.setSendStatus()
                            } else {
                                this.dotPadLine.setReceiveAck(false)
                            }

                            if (this.currentIndex >= 1 && this.refreshCount === 0) {
                                let sendData = DotPadSendMakeModel.getSendData(this.sendType, this.dotPadLineList, this.dotPadLine)
                                
                                if (this.sendType === DotPadSendType.ALL_LINE || this.sendType === DotPadSendType.ALL_PARTIAL) {
                                    for (let i = this.currentIndex; i < this.dotPadLineList.length; i++) {
                                        this.dotPadLineList[i].setRequestReady(false);
                                    }
                                }
                                
                                await this.dotPad.writeCmdTest(sendData);
                            } else {
                                let sendData = DotPadSendMakeModel.makeSingleLineData(this.dotPadLine)
                                await this.dotPad.writeCmdTest(sendData);
                            }
                            
                            this.dotCommandSendReady = false;
                            
                            // 최초출력 완료 체크용
                            if (this.refreshCount === 0){
                                this.baseDisplayComplete = false;
                            }
                            
                            // 시간체크 시작
                            this.sendTime = new Date()
                            this.sendCnt = 0
                        }
                        // ACK 수신 대기중
                        else if (!this.dotPadLine.getReceiveAck()) {
                            // 시간체크
                            const nowTime = new Date() - this.sendTime;
                            let time = (() => {
                                const requestTime = this.dotPadLine?.getRequestTime?.() ?? 500;
                                if (
                                    this.currentIndex >= 1
                                    && this.refreshCount === 0
                                    && (
                                        this.sendType === DotPadSendType.ALL_LINE
                                        || this.sendType === DotPadSendType.ALL_PARTIAL
                                    )
                                ) {
                                    if (cellType === "D2") 
                                        return (requestTime * this.dotPadLineList.length);
                                    else 
                                        return 600;
                                } else {
                                    return requestTime;
                                }
                            })();

                            if (nowTime > time) {
                                // 재요청
                                if (this.sendCnt < 3) {
                                    // 재발송
                                    if (this.currentIndex >= 1 && this.refreshCount === 0) {
                                        if (this.sendType === DotPadSendType.ALL_LINE || this.sendType === DotPadSendType.ALL_PARTIAL) {
                                            // 이미 발송한걸로 취급되서 재발송시에 requestReady를 세팅
                                            for (let i = this.currentIndex; i < this.dotPadLineList.length; i++) {
                                                this.dotPadLineList[i].setRequestReady(true);
                                            }
                                        }

                                        let sendData = DotPadSendMakeModel.getSendData(this.sendType, this.dotPadLineList, this.dotPadLine)
                                        await this.dotPad.writeCmdTest(sendData);

                                        if (this.sendType === DotPadSendType.ALL_LINE || this.sendType === DotPadSendType.ALL_PARTIAL) {
                                            // 발송 후 다시 false 처리
                                            for (let i = this.currentIndex; i < this.dotPadLineList.length; i++) {
                                                this.dotPadLineList[i].setRequestReady(false);
                                            }
                                        }
                                    } else {
                                        let sendData = DotPadSendMakeModel.makeSingleLineData(this.dotPadLine)
                                        await this.dotPad.writeCmdTest(sendData);
                                    }
                                    this.sendTime = new Date();
                                    this.sendCnt += 1;
                                } else {
                                    // 수신받은 것으로 처리
                                    this.dotPadLine.setReceiveAck(true);
                                    this.dotCommandSendReady = true;
                                    this.baseDisplayComplete = true;
                                }
                            }
                        } else if (this.dotCommandSendReady) {
                            if (this.currentIndex >= 1 && this.refreshCount === 0) {
                                if (this.sendType === DotPadSendType.ALL_LINE || this.sendType === DotPadSendType.ALL_PARTIAL) {
                                    this.currentIndex = this.dotPadLineList.length
                                    this.dotPadLine = null
                                } else {
                                    this.currentIndex += 1;
                                }
                            } else {
                                this.currentIndex += 1;
                            }
                        } else {
                            this.dotCommandSendReady = true;
                        }
                    }

                    // DotPad320 D2셀의 경우 Refresh 처리
                    if (cellType === "D2")
                        this.refreshCommand()
                    
                    setTimeout(processNextLine, 1) // 0.001초 지연 후 다음 반복 실행
                }
                else {
                    this.isFunctionRunning = false
                    this.refreshMode = RefreshMode.NONE
                    this.baseDisplayComplete = true
                }
            }
            
            try {
                await processNextLine()
            }
            catch (e) {
                console.log("sendCommand Error: ", e)
            }
        }
        else {
            this.restart = true
        }
    }

    // Ack 세팅
    setDotPadLineReceiveAck(line, receiveAck) {
        if (line > 0 && (this.sendType === DotPadSendType.ALL_LINE || this.sendType === DotPadSendType.ALL_PARTIAL)) {
            for (let i = 1; i < this.dotPadLineList.length; i++) {
                this.dotPadLineList[i].setReceiveAck(receiveAck);
            }
        } else {
            const dotPadLine = this.dotPadLineList[line];
            if(dotPadLine){
                dotPadLine.setReceiveAck(receiveAck);
            }
        }
    }

    // 재출력 타입 세팅
    setRefreshType(type){
        this.refreshType = type
    }

    // 재출력
    refreshCommand() {
        if(this.dotPadLineList.length === this.currentIndex) {
            switch (this.refreshType) {
                case RefreshType.TYPE_A : this.refreshCommandFuncA(); break;
                case RefreshType.TYPE_B : this.refreshCommandFuncB(); break;
                case RefreshType.TYPE_C : this.refreshCommandFuncC(); break;
                default: break;
            }
        }
    }

    // 재출력 A타입
    refreshCommandFuncA() {
        // 전체출력후 재출력
        if (this.refreshCount < 3) {
            this.currentIndex = 0;
            
            for (const tempLine of this.dotPadLineList) {
                tempLine.refresh();
            }
            
            this.refreshCount += 1;
        }
    }

    // 재출력 B타입
    refreshCommandFuncB() {
        // 전체출력후 재출력
        if (this.refreshCount < 3) {
            this.currentIndex = 0;
            for (let i = 0; i <= 3; i++) {
                this.dotPadLineList[i].refresh();
            }
        } else if (this.refreshCount < 6) {
            this.currentIndex = 1;
            
            for (let i = 4; i <= 10; i++) {
                this.dotPadLineList[i].refresh();
            }
        } else {
            this.refreshMode = RefreshMode.NONE;
        }
        
        this.refreshCount += 1;
    }

    // 재출력 C타입
    refreshCommandFuncC() {
        // 재출력 작업이 등록되어 있는경우
        if (this.refreshItem != null) {
            return
        }

        switch (this.refreshMode) {
            case RefreshMode.ALL_DISPLAY : this.refreshCommandFuncB(); break;
            case RefreshMode.GRAPHIC_DISPLAY : this.graphicRefresh(); break;
            case RefreshMode.TEXT_DISPLAY : this.textRefresh(); break;
            case RefreshMode.LIVE_DISPLAY : this.liveRefresh(); break;
            default:break;
        }
    }

    graphicRefresh() {
        if (this.refreshCount < 3) {
            this.currentIndex = 1

            for (let i = 1; i <= 3; i++) {
                this.dotPadLineList[i].refresh();
            }

            this.refreshCount += 1;
        } else if (this.refreshCount < 6) {
            this.refreshItem = () => {
                this.currentIndex = 1;

                for (let i = 4; i <= 10; i++) {
                    this.dotPadLineList[i].refresh();
                }

                this.refreshItem = null;
                this.refreshCount += 1;
                this.sendCommand(true);
            };

            setTimeout(this.refreshItem, 500);
        } else {
            this.refreshMode = RefreshMode.NONE;
        }
    }

    textRefresh(){
        if (this.refreshCount < 3) {
            this.refreshItem = () => {
                this.currentIndex = 0;
                this.dotPadLineList[0].refresh();
                
                this.refreshItem = null;
                this.refreshCount += 1;
                this.sendCommand(true);
            }
            
            setTimeout(this.refreshItem, 1500);
        } else {
            this.refreshMode = RefreshMode.NONE;
        }
    }

    liveRefresh(){
        if (this.refreshCount < 3) {
            this.refreshItem = () => {
                this.currentIndex = 0;
                
                for (const i of this.refreshLine) {
                    this.dotPadLineList[i].refresh(RefreshMode.LIVE_DISPLAY);
                }
                
                this.refreshItem = null;
                this.refreshCount += 1;
                this.sendCommand(true);
            }
            
            setTimeout(this.refreshItem, 1500);
        } else {
            this.refreshLine = [];
            this.refreshMode = RefreshMode.NONE;
        }
    }

    setRefreshMode(mode) {
        clearTimeout(this.refreshItem)
        this.refreshItem = null
        
        if (this.refreshMode !== RefreshMode.LIVE_DISPLAY && mode === RefreshMode.LIVE_DISPLAY) {
            this.refreshLine = []
        }
        
        if (this.refreshMode === RefreshMode.NONE) {
            this.refreshMode = mode
            this.refreshLine = []
        }
        else if (this.refreshMode !== RefreshMode.ALL_DISPLAY && this.refreshMode !== mode) {
            if (this.refreshMode === RefreshMode.GRAPHIC_DISPLAY) {
                if (mode === RefreshMode.TEXT_DISPLAY) {
                    this.refreshMode = RefreshMode.ALL_DISPLAY
                }
            }
            else if (this.refreshMode === RefreshMode.TEXT_DISPLAY) {
                if (mode !== RefreshMode.TEXT_DISPLAY) {
                    this.refreshMode = RefreshMode.ALL_DISPLAY
                }
            }
            else if (this.refreshMode === RefreshMode.LIVE_DISPLAY) {
                if (mode === RefreshMode.TEXT_DISPLAY) {
                    this.refreshMode = RefreshMode.ALL_DISPLAY
                }
                else if (mode === RefreshMode.GRAPHIC_DISPLAY) {
                    this.refreshMode = RefreshMode.GRAPHIC_DISPLAY
                }
            }
        }
    }

    compareString(str1, str2) {
        let start = -1;
        let end = -1;
    
        for (let i = 0; i < str1.length - 1; i += 2) {
            const char1 = str1.substring(i, i + 2);
            const char2 = str2.substring(i, i + 2);
    
            if (char1 !== char2) {
                if (start === -1) {
                    start = i;
                }
                end = i + 1;
            }
        }
    
        if (start !== -1) {
            return [start, end];
        } else {
            return null;
        }
    }
}

export {
    DotPadSendModule,
    RefreshMode
}
